import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import BracketTreeView from '../components/BracketTreeView';

/**
 * Dev-only visual harness for the BracketTreeView.
 * Mounts the component with synthetic tournament data for 2/3/5/7/9 team brackets
 * so designers can validate the CSS connectors and dashed bye slots without
 * seeding a real tournament in MongoDB.
 *
 * Route: /dev/bracket-demo
 */
export default function BracketDemo() {
  const [scenario, setScenario] = useState('5');

  const scenarios = {
    '2': build2Teams(),
    '3': build3Teams(),
    '4': build4Teams(),
    '5': build5Teams(),
    '7': build7Teams(),
    '9': build9Teams(),
    'mid': buildMidTournament(),
    'done': buildCompletedTournament(),
  };

  const { tournament, matches } = scenarios[scenario];

  const teamNames = {};
  (tournament.teams || []).forEach(t => { teamNames[t.team_id] = t.name; });
  const getTeamName = (tid) => teamNames[tid] || tid || '—';

  return (
    <Layout>
      <div className="space-y-4 p-4">
        <h1 className="text-2xl font-heading font-bold">Bracket Tree Visual Harness</h1>
        <p className="text-sm text-muted-foreground">
          Synthetic data — pick a scenario to validate connector alignment and bye rendering.
        </p>
        <div className="flex flex-wrap gap-2">
          {Object.keys(scenarios).map(key => (
            <button
              key={key}
              onClick={() => setScenario(key)}
              className={`px-3 py-1.5 rounded border text-sm font-semibold ${
                scenario === key ? 'bg-accent text-accent-foreground border-accent' : 'bg-card hover:border-accent'
              }`}
              data-testid={`scenario-${key}`}
            >
              {key === 'mid' ? 'In-Progress' : key === 'done' ? 'Completed' : `${key} Teams`}
            </button>
          ))}
        </div>

        <div className="rounded-lg border bg-card p-4">
          <BracketTreeView
            tournament={tournament}
            matches={matches}
            getTeamName={getTeamName}
          />
        </div>

        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer">Raw tournament.knockout JSON</summary>
          <pre className="mt-2 overflow-x-auto rounded bg-muted p-3 text-xs">
            {JSON.stringify(tournament.knockout, null, 2)}
          </pre>
        </details>
      </div>
    </Layout>
  );
}

/* ─── synthetic data builders ─── */

function mkTeams(n) {
  return Array.from({ length: n }, (_, i) => ({
    team_id: `t${i + 1}`,
    name: `Team ${i + 1}`,
  }));
}

function build2Teams() {
  return {
    tournament: {
      tournament_id: 'demo-2',
      teams: mkTeams(2),
      knockout: {
        bracket_size: 2,
        rounds: [{
          round: 'Final',
          match_ids: ['m1'],
          byes: [],
          advancements: [{ type: 'match', match_id: 'm1' }],
        }],
      },
    },
    matches: [
      { match_id: 'm1', team1_id: 't1', team2_id: 't2', scores: [], status: 'pending', winner_id: null, round: 'Final' },
    ],
  };
}

function build3Teams() {
  return {
    tournament: {
      tournament_id: 'demo-3',
      teams: mkTeams(3),
      knockout: {
        bracket_size: 4,
        rounds: [{
          round: 'Semi Final',
          match_ids: ['m1'],
          byes: ['t1'],
          advancements: [
            { type: 'bye', team_id: 't1' },
            { type: 'match', match_id: 'm1' },
          ],
        }],
      },
    },
    matches: [
      { match_id: 'm1', team1_id: 't2', team2_id: 't3', scores: [], status: 'pending', winner_id: null, round: 'Semi Final' },
    ],
  };
}

function build4Teams() {
  return {
    tournament: {
      tournament_id: 'demo-4',
      teams: mkTeams(4),
      knockout: {
        bracket_size: 4,
        rounds: [{
          round: 'Semi Final',
          match_ids: ['m1', 'm2'],
          byes: [],
          advancements: [
            { type: 'match', match_id: 'm1' },
            { type: 'match', match_id: 'm2' },
          ],
        }],
      },
    },
    matches: [
      { match_id: 'm1', team1_id: 't1', team2_id: 't4', scores: [], status: 'pending', winner_id: null, round: 'Semi Final' },
      { match_id: 'm2', team1_id: 't2', team2_id: 't3', scores: [], status: 'pending', winner_id: null, round: 'Semi Final' },
    ],
  };
}

function build5Teams() {
  return {
    tournament: {
      tournament_id: 'demo-5',
      teams: mkTeams(5),
      knockout: {
        bracket_size: 8,
        rounds: [{
          round: 'Quarter Final',
          match_ids: ['m1'],
          byes: ['t1', 't2', 't3'],
          advancements: [
            { type: 'bye', team_id: 't1' },
            { type: 'match', match_id: 'm1' },
            { type: 'bye', team_id: 't2' },
            { type: 'bye', team_id: 't3' },
          ],
        }],
      },
    },
    matches: [
      { match_id: 'm1', team1_id: 't4', team2_id: 't5', scores: [], status: 'pending', winner_id: null, round: 'Quarter Final' },
    ],
  };
}

function build7Teams() {
  return {
    tournament: {
      tournament_id: 'demo-7',
      teams: mkTeams(7),
      knockout: {
        bracket_size: 8,
        rounds: [{
          round: 'Quarter Final',
          match_ids: ['m1', 'm2', 'm3'],
          byes: ['t1'],
          advancements: [
            { type: 'bye', team_id: 't1' },
            { type: 'match', match_id: 'm1' },
            { type: 'match', match_id: 'm2' },
            { type: 'match', match_id: 'm3' },
          ],
        }],
      },
    },
    matches: [
      { match_id: 'm1', team1_id: 't4', team2_id: 't5', scores: [], status: 'pending', winner_id: null, round: 'Quarter Final' },
      { match_id: 'm2', team1_id: 't2', team2_id: 't7', scores: [], status: 'pending', winner_id: null, round: 'Quarter Final' },
      { match_id: 'm3', team1_id: 't3', team2_id: 't6', scores: [], status: 'pending', winner_id: null, round: 'Quarter Final' },
    ],
  };
}

function build9Teams() {
  return {
    tournament: {
      tournament_id: 'demo-9',
      teams: mkTeams(9),
      knockout: {
        bracket_size: 16,
        rounds: [{
          round: 'Round of 16',
          match_ids: ['m1'],
          byes: ['t1', 't4', 't5', 't2', 't7', 't3', 't6'],
          advancements: [
            { type: 'bye', team_id: 't1' },
            { type: 'match', match_id: 'm1' },
            { type: 'bye', team_id: 't4' },
            { type: 'bye', team_id: 't5' },
            { type: 'bye', team_id: 't2' },
            { type: 'bye', team_id: 't7' },
            { type: 'bye', team_id: 't3' },
            { type: 'bye', team_id: 't6' },
          ],
        }],
      },
    },
    matches: [
      { match_id: 'm1', team1_id: 't8', team2_id: 't9', scores: [], status: 'pending', winner_id: null, round: 'Round of 16' },
    ],
  };
}

function buildMidTournament() {
  // 8-team tournament with QF done, SF half-played
  return {
    tournament: {
      tournament_id: 'demo-mid',
      teams: mkTeams(8),
      knockout: {
        bracket_size: 8,
        rounds: [
          {
            round: 'Quarter Final',
            match_ids: ['qf1', 'qf2', 'qf3', 'qf4'],
            byes: [],
            advancements: [
              { type: 'match', match_id: 'qf1' },
              { type: 'match', match_id: 'qf2' },
              { type: 'match', match_id: 'qf3' },
              { type: 'match', match_id: 'qf4' },
            ],
          },
          {
            round: 'Semi Final',
            match_ids: ['sf1', 'sf2'],
            byes: [],
            advancements: [
              { type: 'match', match_id: 'sf1' },
              { type: 'match', match_id: 'sf2' },
            ],
          },
        ],
      },
    },
    matches: [
      { match_id: 'qf1', team1_id: 't1', team2_id: 't8', scores: [{ winner: 'team1' }, { winner: 'team1' }], status: 'completed', winner_id: 't1', round: 'Quarter Final' },
      { match_id: 'qf2', team1_id: 't4', team2_id: 't5', scores: [{ winner: 'team2' }, { winner: 'team2' }], status: 'completed', winner_id: 't5', round: 'Quarter Final' },
      { match_id: 'qf3', team1_id: 't2', team2_id: 't7', scores: [{ winner: 'team1' }, { winner: 'team2' }, { winner: 'team1' }], status: 'completed', winner_id: 't2', round: 'Quarter Final' },
      { match_id: 'qf4', team1_id: 't3', team2_id: 't6', scores: [{ winner: 'team1' }, { winner: 'team1' }], status: 'completed', winner_id: 't3', round: 'Quarter Final' },
      { match_id: 'sf1', team1_id: 't1', team2_id: 't5', scores: [{ winner: 'team1' }], status: 'in_progress', winner_id: null, round: 'Semi Final' },
      { match_id: 'sf2', team1_id: 't2', team2_id: 't3', scores: [], status: 'pending', winner_id: null, round: 'Semi Final' },
    ],
  };
}

function buildCompletedTournament() {
  // 4-team tournament, fully completed → champion shown
  return {
    tournament: {
      tournament_id: 'demo-done',
      teams: mkTeams(4),
      knockout: {
        bracket_size: 4,
        rounds: [
          {
            round: 'Semi Final',
            match_ids: ['sf1', 'sf2'],
            byes: [],
            advancements: [
              { type: 'match', match_id: 'sf1' },
              { type: 'match', match_id: 'sf2' },
            ],
          },
          {
            round: 'Final',
            match_ids: ['f1'],
            byes: [],
            advancements: [{ type: 'match', match_id: 'f1' }],
          },
        ],
      },
    },
    matches: [
      { match_id: 'sf1', team1_id: 't1', team2_id: 't4', scores: [{ winner: 'team1' }, { winner: 'team1' }], status: 'completed', winner_id: 't1', round: 'Semi Final' },
      { match_id: 'sf2', team1_id: 't2', team2_id: 't3', scores: [{ winner: 'team1' }, { winner: 'team2' }, { winner: 'team1' }], status: 'completed', winner_id: 't2', round: 'Semi Final' },
      { match_id: 'f1', team1_id: 't1', team2_id: 't2', scores: [{ winner: 'team1' }, { winner: 'team2' }, { winner: 'team1' }], status: 'completed', winner_id: 't1', round: 'Final' },
    ],
  };
}
